import { useMutation, useQuery } from 'react-query'

import { queryClient } from '@client/init'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useAuthentication } from '@hooks/useAuthentication'
import { useUserAction } from '@hooks/user/useUserAction'
import { FollowSuccessStatusEnum } from '@interfaces/FollowSuccessStatusEnum'
import { useAlert } from '@hooks/useAlert'
import { ReadingPageFooterProps, WithReadingPageFooterProps } from './interface'

export function withReadingPageFooter(
  Component: React.FC<ReadingPageFooterProps>
) {
  function WithReadingPageFooter({
    penNameId,
    penNameUserId,
    ...props
  }: WithReadingPageFooterProps) {
    const userClient = useUserAction()
    const { isAuthenticated, user } = useAuthentication()
    const alert = useAlert()
    const loginModal = useModal({
      modal: 'login',
    })
    const canFollow = !!(penNameUserId && penNameUserId !== user.id)

    const { data: isFollowedPenName = false } = useQuery(
      ['followed', 'pen-name', penNameId],
      () => userClient.checkIsFollowedPenName(penNameId!),
      {
        enabled: !!penNameId && isAuthenticated && canFollow,
      }
    )

    const { data: isFollowedUser = false } = useQuery(
      ['followed', 'user', penNameUserId],
      () => userClient.checkIsFollowedUser(penNameUserId!),
      {
        enabled: !!penNameUserId && isAuthenticated && canFollow,
      }
    )

    const { mutate: toggleFollowUser, isLoading: isToggleFollowUserLoading } =
      useMutation((userId: number) => userClient.toggleFollowUser(userId), {
        onSuccess: (type: FollowSuccessStatusEnum) => {
          queryClient.setQueryData<boolean>(
            ['followed', 'user', penNameUserId],
            () => {
              return type === FollowSuccessStatusEnum.CREATE
            }
          )
        },
        onError: () => {
          alert.error('บัญชีผู้ใช้นี้ถูกลบไปแล้ว ไม่สามารถติดตามได้')
        },
      })

    const {
      mutate: toggleFollowPenName,
      isLoading: isToggleFollowPenNameLoading,
    } = useMutation((id: number) => userClient.toggleFollowPenName(id), {
      onSuccess: (type: FollowSuccessStatusEnum) => {
        queryClient.setQueryData<boolean>(
          ['followed', 'pen-name', penNameId],
          () => {
            return type === FollowSuccessStatusEnum.CREATE
          }
        )
      },
      onError: () => {
        alert.error('บัญชีผู้ใช้นี้ถูกลบไปแล้ว ไม่สามารถติดตามได้')
      },
    })

    function handleFollowUser() {
      if (isAuthenticated && penNameUserId) {
        toggleFollowUser(penNameUserId)
      } else {
        loginModal.show()
      }
    }

    function handleFollowPenName() {
      if (isAuthenticated && penNameId) {
        toggleFollowPenName(penNameId)
      } else {
        loginModal.show()
      }
    }

    const componentProps = {
      ...props,
      canFollow,
      isFollowedUser,
      isFollowedPenName,
      isToggleFollowPenNameLoading,
      isToggleFollowUserLoading,
      handleFollowUser,
      handleFollowPenName,
    }

    return <Component {...componentProps} />
  }

  return WithReadingPageFooter
}
